import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BookOpen, Download, ChevronDown, ChevronRight, MapPin,
  Clock, FileText, DollarSign, Users, Shield, Settings,
  Fingerprint, Calendar, BarChart3, UserPlus, Award,
  Building2, ClipboardList, TrendingUp, Megaphone,
  FolderOpen, LogOut, CheckCircle, XCircle, AlertTriangle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
/* ====================================================================
   ACCORDION SECTION COMPONENT
   ==================================================================== */
function Section({ icon: Icon, title, badge, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`manual-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-4 bg-white hover:bg-slate-50 text-left transition-colors">
        {Icon && <Icon className="w-5 h-5 text-[#DC2626] flex-shrink-0" />}
        <span className="flex-1 font-semibold text-slate-800 text-sm">{title}</span>
        {badge && <Badge className="bg-blue-100 text-blue-700 text-[10px]">{badge}</Badge>}
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 pb-4 bg-white border-t border-slate-100">{children}</div>}
    </div>
  );
}

function Step({ num, title, desc }) {
  return (
    <div className="flex gap-3 py-2">
      <div className="w-7 h-7 rounded-full bg-[#DC2626] text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{num}</div>
      <div>
        <p className="text-sm font-medium text-slate-800">{title}</p>
        {desc && <p className="text-xs text-slate-500 mt-0.5">{desc}</p>}
      </div>
    </div>
  );
}

function Tip({ type = 'info', children }) {
  const styles = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-green-50 border-green-200 text-green-800',
  };
  const icons = { info: AlertTriangle, warning: AlertTriangle, success: CheckCircle };
  const TipIcon = icons[type];
  return (
    <div className={`flex gap-2 p-3 rounded-lg border text-xs mt-3 ${styles[type]}`}>
      <TipIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
}

/* ====================================================================
   EMPLOYEE MANUAL CONTENT
   ==================================================================== */
function EmployeeManual() {
  return (
    <div className="space-y-3">
      <Section icon={LogOut} title="1. Login & First Steps" defaultOpen={true}>
        <div className="space-y-1 mt-3">
          <Step num={1} title="Open the HRMS Portal" desc="Go to the HRMS URL in your browser (works on phone and desktop)" />
          <Step num={2} title="Enter Your Credentials" desc="Email: Your registered email address. Password: Provided by HR (format: first 4 chars of your name + last 4 of your Employee ID)" />
          <Step num={3} title="Click 'Sign In'" desc="You'll be redirected to the Dashboard showing your attendance, leave balance, and upcoming events" />
          <Step num={4} title="Change Your Password (Recommended)" desc="Go to Settings (bottom of sidebar) to change your default password" />
        </div>
        <Tip type="info">If you forgot your password, contact HR. They can reset it from the Admin Panel.</Tip>
      </Section>

      <Section icon={MapPin} title="2. Mark Attendance (Geo-Fence)" badge="Daily">
        <div className="space-y-1 mt-3">
          <Step num={1} title="Go to Attendance" desc="Click 'Attendance' in the sidebar. You'll see the 'My Attendance' tab." />
          <Step num={2} title="Allow Location Access" desc="Your browser will ask for GPS permission. Click 'Allow'. This is required for geo-fence verification." />
          <Step num={3} title="Click 'Check In'" desc="The system checks your GPS coordinates against the campus boundary (500m radius). If you're inside campus, attendance is marked." />
          <Step num={4} title="Check Out at End of Day" desc="Come back to Attendance and click 'Check Out'. Work hours are automatically calculated." />
        </div>
        <Tip type="warning">
          <strong>Strict Mode Active:</strong> You MUST be physically inside the campus (within 500m of campus center) to check in.
          If you're outside, the system will block your check-in with a distance error.
        </Tip>
        <Tip type="info">
          <strong>Late Marking:</strong> If you check in after 09:10 AM (9:00 AM + 10 min grace), your attendance is marked as "Late".
          Overtime is calculated if you work beyond 8 hours.
        </Tip>
      </Section>

      <Section icon={Calendar} title="3. Apply for Leave">
        <div className="space-y-1 mt-3">
          <Step num={1} title="Go to Leaves" desc="Click 'Leaves' in the sidebar." />
          <Step num={2} title="Check Your Leave Balance" desc="You can see remaining leaves for each type (Casual, Sick, Earned, etc.) at the top." />
          <Step num={3} title="Click 'Apply for Leave'" desc="Select leave type, start date, end date, and provide a reason." />
          <Step num={4} title="Submit" desc="Your leave request goes to your HOD for approval. You'll see the status in your leave history." />
          <Step num={5} title="Track Status" desc="Check the 'My Leaves' tab to see if your leave is Pending, Approved, or Rejected." />
        </div>
        <Tip type="info">Leave policies are set by HR. Different employees may have different leave allocations based on their policy.</Tip>
      </Section>

      <Section icon={DollarSign} title="4. View Payslip & Salary">
        <div className="space-y-1 mt-3">
          <Step num={1} title="Go to Payroll" desc="Click 'Payroll' in the sidebar." />
          <Step num={2} title="View Salary Records" desc="See your monthly salary breakdown: Basic, HRA, DA, Allowances, PF, ESI, TDS deductions." />
          <Step num={3} title="Download Salary Slip" desc="Click the download icon next to any month to get a PDF salary slip." />
          <Step num={4} title="Form 16" desc="Go to the 'Form 16' tab to view and download your annual tax form." />
          <Step num={5} title="PF & ESI Details" desc="The 'PF/ESI' tab shows your provident fund and insurance contribution details." />
        </div>
      </Section>

      <Section icon={FolderOpen} title="5. Documents">
        <div className="space-y-1 mt-3">
          <Step num={1} title="Go to Documents" desc="Click 'Documents' in the sidebar." />
          <Step num={2} title="Upload Documents" desc="Upload personal documents (Aadhar, PAN, certificates) for HR records." />
          <Step num={3} title="View Uploaded" desc="See all your uploaded documents with download links." />
        </div>
      </Section>

      <Section icon={Megaphone} title="6. Notices & Announcements">
        <div className="space-y-1 mt-3">
          <Step num={1} title="Go to Notices" desc="Click 'Notices' in the sidebar to see all announcements." />
          <Step num={2} title="View Details" desc="Click on any notice to see full details, attachments, and who issued it." />
        </div>
      </Section>

      <Section icon={Award} title="7. Performance & Appraisals">
        <div className="space-y-1 mt-3">
          <Step num={1} title="Go to Performance" desc="Click 'Performance' in the sidebar." />
          <Step num={2} title="View Your Appraisals" desc="See past and current performance reviews, ratings, and feedback." />
          <Step num={3} title="AICTE/UGC Scores" desc="If applicable, view your API score calculations under L&D module." />
        </div>
      </Section>

      <Section icon={Settings} title="8. Settings & Profile">
        <div className="space-y-1 mt-3">
          <Step num={1} title="Change Password" desc="Go to Settings → enter current password → enter new password → Save." />
          <Step num={2} title="Upload Profile Photo" desc="Go to Settings → click on avatar → select image → Upload." />
          <Step num={3} title="View Profile" desc="Your profile information (name, email, department, entity) is shown in Settings." />
        </div>
      </Section>
    </div>
  );
}

/* ====================================================================
   HR MANUAL CONTENT
   ==================================================================== */
function HRManual() {
  return (
    <div className="space-y-3">
      <Section icon={Users} title="1. Employee Management" defaultOpen={true}>
        <div className="space-y-1 mt-3">
          <Step num={1} title="View All Employees" desc="Go to 'Employees' → see full list with search, filter by department/entity." />
          <Step num={2} title="Add New Employee" desc="Click 'Add Employee' → fill in personal details, department, designation, salary components → Save." />
          <Step num={3} title="Bulk Upload" desc="Click 'Bulk Upload' → download CSV template → fill in → upload. System creates employees in batch." />
          <Step num={4} title="Edit Employee" desc="Click on any employee → update their details, department, salary, etc." />
          <Step num={5} title="Employee Exit" desc="On employee detail page → click 'Process Exit' → select exit date and reason → generate relieving letter." />
        </div>
        <Tip type="info">After adding employees, use "Admin Panel → Bulk Create Accounts" to generate login credentials for all new employees automatically.</Tip>
      </Section>

      <Section icon={Fingerprint} title="2. ESSL Biometric & Attendance">
        <div className="space-y-1 mt-3">
          <h4 className="text-xs font-bold text-slate-700 mt-2 mb-1">A. ESSL Device Setup</h4>
          <Step num={1} title="Go to Attendance → ESSL Devices tab" desc="Register your ESSL biometric machine here." />
          <Step num={2} title="Add Device" desc="Click '+ Add Device' → select 'ICLOCK Push' protocol → enter device IP (172.168.63.74), port, serial number." />
          <Step num={3} title="Map Employees" desc="Click 'Map' on the device card → click '+ Add Mapping' → enter ESSL User ID → select HRMS employee." />
          <Step num={4} title="Sync Attendance" desc="Option A: Download & run the Local Sync Agent on campus network. Option B: Export CSV from eTimeTrackLite → upload via CSV button." />

          <h4 className="text-xs font-bold text-slate-700 mt-4 mb-1">B. Geo-Fence Configuration</h4>
          <Step num={5} title="Go to Attendance → Geo-Fence tab" desc="Configure campus boundary for GPS-based attendance." />
          <Step num={6} title="Set Campus Center" desc="Enter Latitude, Longitude, and Radius (in meters) of the campus." />
          <Step num={7} title="Enable Strict Mode" desc="When ON, employees CANNOT check in from outside the geo-fence. When OFF, they can but it's flagged." />

          <h4 className="text-xs font-bold text-slate-700 mt-4 mb-1">C. Manual Attendance</h4>
          <Step num={8} title="Mark Attendance Tab" desc="Go to 'Mark Attendance' tab to manually mark biometric attendance for employees." />
          <Step num={9} title="Team Records" desc="View all employee attendance records, filter by date range, export." />
        </div>
      </Section>

      <Section icon={Calendar} title="3. Leave Management">
        <div className="space-y-1 mt-3">
          <Step num={1} title="Review Leave Requests" desc="Go to 'Leaves' → see all pending requests. Approve or reject with comments." />
          <Step num={2} title="Leave Policy" desc="Go to 'Leave Policy' → create templates (e.g., 12 CL + 8 SL + 10 EL per year). Assign policies to employees." />
          <Step num={3} title="Track Balances" desc="View any employee's leave balance. Adjust manually if needed." />
        </div>
      </Section>

      <Section icon={DollarSign} title="4. Payroll Processing">
        <div className="space-y-1 mt-3">
          <Step num={1} title="Generate Payroll" desc="Go to 'Payroll' → select month → click 'Generate Payroll' for individual or bulk employees." />
          <Step num={2} title="Review & Edit" desc="Check salary breakdown: Basic, HRA, DA, PF, ESI, TDS. Edit if needed." />
          <Step num={3} title="Send to Finance" desc="After review, payroll goes to Finance Manager for verification." />
          <Step num={4} title="Download Reports" desc="Go to 'Custom Reports' tab → download employee data, bank details, or salary sheets as CSV." />
        </div>
        <Tip type="info">
          <strong>Payroll Workflow:</strong> HR generates → Finance verifies → Finance marks paid → Bank export available.
        </Tip>
      </Section>

      <Section icon={UserPlus} title="5. Recruitment">
        <div className="space-y-1 mt-3">
          <Step num={1} title="Create Recruitment Request" desc="Go to 'Recruitment' → click 'New Request' → fill position, department, requirements." />
          <Step num={2} title="Approval Flow" desc="Request goes: HR → HOD → Principal. Track status in real-time." />
          <Step num={3} title="Onboarding Link" desc="Once approved, a unique onboarding link is generated. Share with the candidate." />
          <Step num={4} title="Candidate Submits Info" desc="Candidate fills personal details, qualifications, documents via the link." />
        </div>
      </Section>

      <Section icon={BarChart3} title="6. Reports & Analytics">
        <div className="space-y-1 mt-3">
          <Step num={1} title="Attendance Analytics" desc="Go to Attendance → Analytics tab for charts on punctuality, absenteeism." />
          <Step num={2} title="Custom Reports" desc="Go to Reports → Custom Reports → download employee data, bank details, salary info." />
          <Step num={3} title="Payroll Dashboard" desc="Go to Payroll → Dashboard tab for salary distribution charts and trends." />
        </div>
      </Section>

      <Section icon={Megaphone} title="7. Notices">
        <div className="space-y-1 mt-3">
          <Step num={1} title="Create Notice" desc="Go to 'Notices' → click 'Create Notice' → select audience (all/entity/department) → add content & attachments." />
          <Step num={2} title="Manage Notices" desc="Edit or delete existing notices. View read receipts." />
        </div>
      </Section>
    </div>
  );
}

/* ====================================================================
   ADMIN / SUPER ADMIN MANUAL CONTENT
   ==================================================================== */
function AdminManual() {
  return (
    <div className="space-y-3">
      <Section icon={Shield} title="1. Admin Panel" defaultOpen={true}>
        <div className="space-y-1 mt-3">
          <h4 className="text-xs font-bold text-slate-700 mb-1">Users Tab</h4>
          <Step num={1} title="View All Users" desc="See all user accounts across institutes with search functionality." />
          <Step num={2} title="Add User" desc="Click '+ Add User' → fill name, email, password, role, institute, department → Create." />
          <Step num={3} title="Edit User" desc="Click edit icon → change name, email, role, active status → Save." />
          <Step num={4} title="Reset Password" desc="Click key icon → enter new password → Reset." />
          <Step num={5} title="Bulk Create Employee Accounts" desc="Creates login accounts for all employees who don't have one. Auto-generates passwords." />

          <h4 className="text-xs font-bold text-slate-700 mt-4 mb-1">Institutes Tab</h4>
          <Step num={6} title="Manage Institutes" desc="Add/edit/delete institutes. Create sub-institutes under parent entities." />

          <h4 className="text-xs font-bold text-slate-700 mt-4 mb-1">Data Management</h4>
          <Step num={7} title="Load Demo Data" desc="Populates the system with sample data for testing." />
          <Step num={8} title="Clear Data" desc="Removes all data (use with caution)." />
        </div>
      </Section>

      <Section icon={Shield} title="2. Role Management">
        <div className="space-y-1 mt-3">
          <Step num={1} title="View All Roles" desc="Go to 'Roles' → see system roles (employee, hr, hod, etc.) and custom roles." />
          <Step num={2} title="Assign Role" desc="Click 'Assign' on any role → search for user → click 'Assign' to change their role." />
          <Step num={3} title="Create Custom Role" desc="Click 'Create Role' → name, code, description → select permissions → Save." />
        </div>
        <Tip type="warning">
          <strong>Be careful</strong> when changing roles. Changing a user from 'hr' to 'employee' will remove their HR access.
        </Tip>
      </Section>

      <Section icon={Building2} title="3. Institute Management">
        <div className="space-y-1 mt-3">
          <Step num={1} title="Go to Institutes" desc="View all institutes with their codes, types, and employee counts." />
          <Step num={2} title="Add Institute" desc="Click 'Add Institute' → name, code, type (College/University), compliance body." />
          <Step num={3} title="Sub-Institutes" desc="Create hierarchical structures (e.g., Departments under Colleges)." />
        </div>
      </Section>

      <Section icon={ClipboardList} title="4. Audit Trail">
        <div className="space-y-1 mt-3">
          <Step num={1} title="Go to Audit Trail" desc="View all system actions: who did what, when, and on which record." />
          <Step num={2} title="Filter & Search" desc="Filter by user, action type, entity, date range." />
          <Step num={3} title="Compliance" desc="Use for regulatory audits. All CRUD operations are logged automatically." />
        </div>
      </Section>

      <Section icon={TrendingUp} title="5. Salary Revisions">
        <div className="space-y-1 mt-3">
          <Step num={1} title="View Revision Requests" desc="Go to 'Salary Revisions' → see pending and approved revision requests." />
          <Step num={2} title="Workflow" desc="Finance proposes revision → Principal approves/rejects → HR implements." />
          <Step num={3} title="History" desc="View salary revision history timeline on employee profiles." />
        </div>
      </Section>
    </div>
  );
}

/* ====================================================================
   FINANCE MANUAL CONTENT
   ==================================================================== */
function FinanceManual() {
  return (
    <div className="space-y-3">
      <Section icon={DollarSign} title="1. Payroll Verification" defaultOpen={true}>
        <div className="space-y-1 mt-3">
          <Step num={1} title="Go to Payroll" desc="You see institute-specific payroll records pending your review." />
          <Step num={2} title="Verify Payroll" desc="Review salary breakdown → click 'Verify' if correct, 'Reject' if issues found." />
          <Step num={3} title="Finalize" desc="After verification, click 'Finalize' to approve for payment." />
          <Step num={4} title="Mark as Paid" desc="Once payment is processed, mark payroll as 'Paid'." />
          <Step num={5} title="Bank Export" desc="Click 'Export Bank Details' to download CSV with bank account numbers for salary transfer." />
        </div>
        <Tip type="info">
          <strong>Workflow:</strong> HR generates → You verify → You finalize → Mark as paid → Bank export
        </Tip>
      </Section>

      <Section icon={TrendingUp} title="2. Salary Revisions">
        <div className="space-y-1 mt-3">
          <Step num={1} title="Propose Revision" desc="Go to 'Salary Revisions' → click 'New Revision' → select employee → enter proposed salary → submit." />
          <Step num={2} title="Approval" desc="Principal reviews and approves/rejects. Once approved, HR can implement the change." />
          <Step num={3} title="Track History" desc="View all past revision requests and their status." />
        </div>
      </Section>

      <Section icon={BarChart3} title="3. Reports">
        <div className="space-y-1 mt-3">
          <Step num={1} title="Custom Reports" desc="Go to Reports → Custom Reports → download salary, bank, or employee data as CSV." />
          <Step num={2} title="Payroll Dashboard" desc="Go to Payroll → Dashboard tab → view salary distribution, monthly trends." />
          <Step num={3} title="PF/ESI Reports" desc="Go to Payroll → PF/ESI tab → view provident fund and insurance details." />
        </div>
      </Section>
    </div>
  );
}

/* ====================================================================
   MAIN USER MANUAL PAGE
   ==================================================================== */
export default function UserManual() {
  const { user } = useAuth();
  const role = user?.role || 'employee';
  const [downloading, setDownloading] = useState(null);

  const handleDownloadPDF = async (targetRole) => {
    setDownloading(targetRole);
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/user-manual/download?role=${targetRole}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SGI_HRMS_Manual_${targetRole}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${targetRole} manual downloaded`);
    } catch (e) { toast.error(e.message); }
    finally { setDownloading(null); }
  };

  const isSuperAdmin = role === 'super_admin';
  const isAdmin = ['super_admin', 'admin'].includes(role);
  const isHR = ['super_admin', 'admin', 'hr'].includes(role);
  const isFinance = ['super_admin', 'finance'].includes(role);

  // Determine default tab
  const defaultTab = isAdmin ? 'admin' : isHR ? 'hr' : isFinance ? 'finance' : 'employee';

  // Available tabs based on role
  const tabs = [];
  tabs.push({ value: 'employee', label: 'Employee Guide', icon: Users });
  if (isHR) tabs.push({ value: 'hr', label: 'HR Guide', icon: UserPlus });
  if (isFinance) tabs.push({ value: 'finance', label: 'Finance Guide', icon: DollarSign });
  if (isAdmin) tabs.push({ value: 'admin', label: 'Admin Guide', icon: Shield });

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="user-manual-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BookOpen className="w-7 h-7 text-[#DC2626]" />
            User Manual & Help Guide
          </h1>
          <p className="text-sm text-slate-500 mt-1">Step-by-step guide for using SGI HRMS. Select your role to see relevant instructions.</p>
        </div>
      </div>

      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${tabs.length}, 1fr)` }}>
          {tabs.map(t => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`manual-tab-${t.value}`}
              className="flex items-center gap-1.5 text-xs sm:text-sm">
              <t.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{t.label}</span>
              <span className="sm:hidden">{t.label.split(' ')[0]}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Employee Tab */}
        <TabsContent value="employee" className="mt-6">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#DC2626]" /> Employee User Manual
                </CardTitle>
                <Button size="sm" variant="outline" onClick={() => handleDownloadPDF('employee')}
                  disabled={downloading === 'employee'} data-testid="download-employee-manual">
                  {downloading === 'employee' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                  Download PDF
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">How to login, mark attendance, apply leave, view payslip, and more.</p>
            </CardHeader>
            <CardContent><EmployeeManual /></CardContent>
          </Card>
        </TabsContent>

        {/* HR Tab */}
        {isHR && (
          <TabsContent value="hr" className="mt-6">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-[#DC2626]" /> HR User Manual
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={() => handleDownloadPDF('hr')}
                    disabled={downloading === 'hr'} data-testid="download-hr-manual">
                    {downloading === 'hr' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                    Download PDF
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Employee management, ESSL integration, leave management, payroll, recruitment, and reports.</p>
              </CardHeader>
              <CardContent><HRManual /></CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Finance Tab */}
        {isFinance && (
          <TabsContent value="finance" className="mt-6">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-[#DC2626]" /> Finance User Manual
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={() => handleDownloadPDF('finance')}
                    disabled={downloading === 'finance'} data-testid="download-finance-manual">
                    {downloading === 'finance' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                    Download PDF
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Payroll verification, salary revisions, reports and exports.</p>
              </CardHeader>
              <CardContent><FinanceManual /></CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Admin Tab */}
        {isAdmin && (
          <TabsContent value="admin" className="mt-6">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#DC2626]" /> Admin / Super Admin Manual
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={() => handleDownloadPDF('admin')}
                    disabled={downloading === 'admin'} data-testid="download-admin-manual">
                    {downloading === 'admin' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                    Download PDF
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Admin Panel, user management, roles, institutes, audit trail, and system governance.</p>
              </CardHeader>
              <CardContent><AdminManual /></CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Quick Reference Card */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Quick Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
            <div className="space-y-1">
              <p className="font-bold text-slate-700">Work Hours</p>
              <p className="text-slate-500">Start: 09:00 AM</p>
              <p className="text-slate-500">End: 05:00 PM</p>
              <p className="text-slate-500">Grace: 10 minutes</p>
            </div>
            <div className="space-y-1">
              <p className="font-bold text-slate-700">Geo-Fence</p>
              <p className="text-slate-500">Campus: Atigre, Kolhapur</p>
              <p className="text-slate-500">Radius: 500 meters</p>
              <p className="text-slate-500">Mode: Strict (block outside)</p>
            </div>
            <div className="space-y-1">
              <p className="font-bold text-slate-700">Support</p>
              <p className="text-slate-500">Forgot password? Contact HR</p>
              <p className="text-slate-500">Technical issue? Contact Admin</p>
              <p className="text-slate-500">GPS not working? Enable Location in phone settings</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
